import { Flex, Select, Space, Text } from '@billy/design-system'
import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_INVOICE_PAYMENT_TERM } from '@features/invoices/constants/defaultInvoicePaymentTerm'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'

import { getCurrentLocale } from '../../../../../../../../../../../../utils'
import { useInvoiceEditorForm } from '../../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../../../../hooks/useInvoiceEditorDocumentType'
import { useIsInvoiceEditorElementRestricted } from '../../../../../../../../hooks/useIsInvoiceEditorElementRestricted'
import { useUpdateDependentDates } from '../../../../../../../../hooks/useUpdateDependentDates'
import { PaymentTermSelectFooter } from './elements/PaymentTermSelectFooter'
import { InvoicePaymentTermValue } from './types/invoicePaymentTermValue'
import { getDisplayDueDateLabel } from './utils/getDisplayDueDateLabel'
import { getItems } from './utils/getItems'

export const InvoicePaymentTermAndDueDate = () => {
  const customPaymentTermRef = useRef<number | undefined>(undefined)

  const { t } = useTranslation()
  const { isQuote } = useInvoiceEditorDocumentType()
  const { data, isLoading } = useZervantSettings()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { FormItem, watch } = invoiceEditorForm
  const { handlePaymentTermChange } = useUpdateDependentDates()
  const isDisabled = useIsInvoiceEditorElementRestricted('paymentTerm', 'disabled')

  const [issueDate, dueDate] = watch(['issueDate', 'dueDate'])
  const locale = getCurrentLocale()
  // A quote only carries the payment term over to the future invoice, so it shows the
  // term alone - its own `dueDate` field is the quote's expiry date and is unrelated.
  const shouldDisplayDueDate = !isQuote

  const paymentTermItems = useMemo(() => {
    const baseTerms = data?.managedFields?.PAYMENT_TERMS ?? []
    return getItems(baseTerms, t, issueDate)
  }, [data?.managedFields?.PAYMENT_TERMS, issueDate, t])

  const handleCustomPaymentTermChange = useCallback(
    (value: number) => {
      customPaymentTermRef.current = value

      trackInvoiceEditor('Form - Payment terms selected', {
        option: 'Custom',
        days: value,
      })
    },
    [trackInvoiceEditor],
  )

  const handleDropdownOpen = useCallback(() => {
    trackInvoiceEditor('Form - Payment terms dropdown opened')
  }, [trackInvoiceEditor])

  const handleDropdownClose = useCallback(
    (onChange: (paymentTerm: number) => void) => () => {
      const customPaymentTerm = customPaymentTermRef.current

      if (customPaymentTerm !== undefined) {
        handlePaymentTermChange(customPaymentTerm)
        onChange?.(customPaymentTerm)
        customPaymentTermRef.current = undefined
      }
    },
    [handlePaymentTermChange],
  )

  const handleSelect = useCallback(
    (onChange: (paymentTerm: number) => void, value?: InvoicePaymentTermValue) => {
      if (value !== undefined) {
        handlePaymentTermChange(value)
        onChange?.(value)
        customPaymentTermRef.current = undefined

        trackInvoiceEditor('Form - Payment terms selected', {
          option: 'Predifined',
          days: value,
        })
      }
    },
    [handlePaymentTermChange, trackInvoiceEditor],
  )

  const getCurrentCustomPaymentTerm = useCallback(
    (value: number) => {
      const activePaymentTerm = paymentTermItems.find((item) => item.value === value)
      return activePaymentTerm?.value ? undefined : value
    },
    [paymentTermItems],
  )

  return (
    <FormItem
      name="paymentTerm"
      label={
        <Flex flexDirection="column">
          <Text variant="h4">{t('external_invoices.editor.form.field.due_date_and_payment_term.header')}</Text>
          <Space />
          <Text colorVariant="secondary">
            {t(
              isQuote
                ? 'external_invoices.editor.form.field.due_date_and_payment_term.quote.label'
                : 'external_invoices.editor.form.field.due_date_and_payment_term.label',
            )}
          </Text>
        </Flex>
      }
      render={({ field: { onChange, value: formValue, ...props } }) => {
        const paymentTermDisplayLabel = getDisplayDueDateLabel({
          dueDate,
          issueDate,
          locale,
          numberOfDaysSelected: formValue,
          t,
          withDueDate: shouldDisplayDueDate,
        })

        // This select uses prefix to show display date as getDisplayValue props only works for list items selection
        return (
          <Select
            {...props}
            defaultSelectedId={String(formValue || DEFAULT_INVOICE_PAYMENT_TERM)}
            disabled={isDisabled}
            dropdownFetching={isLoading}
            dropdownFooter={
              <PaymentTermSelectFooter
                currentPaymentTerm={getCurrentCustomPaymentTerm(formValue)}
                onPaymentTermChange={handleCustomPaymentTermChange}
              />
            }
            dropdownSize="fitTrigger"
            dropdownMaxHeight={426}
            getDisplayValue={() => ''}
            items={paymentTermItems}
            onDropdownClose={handleDropdownClose(onChange)}
            onDropdownOpen={handleDropdownOpen}
            onSelect={(_id?: string, value?: InvoicePaymentTermValue) => handleSelect(onChange, value)}
            placeholder=""
            placement="top"
            prefix={paymentTermDisplayLabel}
            selectedId={String(formValue)}
            selectLook={false}
            withSearch={false}
            size="sm"
          />
        )
      }}
    />
  )
}
